import { useEffect } from "react";

import { useBBASveaAuthenticationGate } from "@bokio/bank/src/bba/useBBASveaAuthenticationGate";
import { SignAndPayGateNotice } from "@bokio/components/SignAndPay/SignAndPayGateNotice/SignAndPayGateNotice";
import { SignAndPayLoginGate } from "@bokio/components/SignAndPay/SignAndPayLoginGate/SignAndPayLoginGate";
import {
	clearPaymentSigningProgressInfoFromLocalStorage,
	readPaymentSigningProgressInfoFromLocalStorage,
	readSveaAuthPollingTokenFromLocalStorage,
	savePaymentSigningProgressInfoToLocalStorage,
} from "@bokio/components/SveaBankIdAuthModal/sveaPollingUtils";
import { useAppContext } from "@bokio/contexts/AppContext/AppContext";
import { Popover } from "@bokio/designsystem/components/Popover/Popover";
import { useDeviceQuery } from "@bokio/elements/DeviceQuery/useDeviceQuery";
import { AnalyticsEventCategory, useMetric } from "@bokio/hooks/useMetric/useMetric";
import { useSignAndPay } from "@bokio/hooks/useSignAndPay/useSignAndPay";
import { useNewSveaAuthentication } from "@bokio/mobile-web-shared/areas/bank/hooks/useNewSveaAuthentication";
import { noop } from "@bokio/shared/utils";

import { SignAndPayFooter } from "../SignAndPayModal/SignAndPayFooter/SignAndPayFooter";
import { SignAndPayHeader } from "../SignAndPayModal/SignAndPayHeader/SignAndPayHeader";
import { SignAndPayModalInner } from "../SignAndPayModal/SignAndPayModal";

import type { BBASveaAuthenticationGateState } from "@bokio/bank/src/bba/useBBASveaAuthenticationGate";
import type { RestrictedAccessState, useSignAndPayReturn } from "@bokio/hooks/useSignAndPay/useSignAndPay";
import type * as m from "@bokio/mobile-web-shared/core/model/model";
import type { FC, ReactNode, RefObject } from "react";

import * as styles from "./signAndPayPopover.scss";

type PaymentValidationRulesDto = m.Bokio.Bank.Contract.Dtos.PaymentValidationRulesDto;
export interface SignAndPayPopoverProps {
	toggleRef: RefObject<HTMLDivElement | null>;
	visible: boolean;
	arrowAnchor?: HTMLElement | null;
	onClose: () => void;
	paymentValidationRules: PaymentValidationRulesDto;
	testId?: string;
}

type SignAndPayPopoverInnerProps = SignAndPayPopoverProps &
	useSignAndPayReturn & {
		forceDisableSign: boolean;
		hideSignButton: boolean;
		gateType: BBASveaAuthenticationGateState["type"];
		restrictedAccessState: RestrictedAccessState;
		gateContent?: ReactNode;
	};

const SignAndPayPopoverInner = (props: SignAndPayPopoverInnerProps) => {
	const { isBankAuthModalOpen } = useAppContext();
	const onClose = () => {
		props.stopPollingAndCancelSigning();
		clearPaymentSigningProgressInfoFromLocalStorage();
		props.onClose();
	};

	const handleOnSignAndPlayClick = async () => {
		savePaymentSigningProgressInfoToLocalStorage({
			hasInitiated: true,
			isPolling: false,
			SuccessfulPaymentIds: undefined,
			paymentGroupIds: props.selectedPaymentGroupIds,
			signingToken: undefined,
		});
		await props.executeAuthorize();
	};

	return (
		<Popover
			toggleRef={props.toggleRef}
			isOpen={props.visible}
			align={"middle"}
			direction={"down"}
			borderRadius={"medium"}
			showArrow
			arrowAnchor={props.arrowAnchor}
			onClose={onClose}
			testId={props.testId}
			clickOutsideCondition={() => !isBankAuthModalOpen}
		>
			<div className={styles.popoverContent}>
				<SignAndPayHeader />
				<div className={styles.signAndPayContent}>{props.renderSignAndPayContent(onClose)}</div>
				<SignAndPayFooter
					style="popover"
					disabled={props.disableSign || props.forceDisableSign}
					onClose={onClose}
					onSignAndPayClick={handleOnSignAndPlayClick}
					multipleCurrencies={props.multipleCurrencies}
					totalSum={props.totalSum}
					selectedPayments={props.selectedPaymentGroupIds.length}
					isLoading={props.isAnyRequestLoading}
					hideSignButton={props.hideSignButton}
					noticeContent={
						<SignAndPayGateNotice gateType={props.gateType} restrictedAccessState={props.restrictedAccessState} />
					}
					gateContent={props.gateContent}
				/>
			</div>
		</Popover>
	);
};

export const SignAndPayPopover: FC<SignAndPayPopoverProps> = props => {
	const { isMobile } = useDeviceQuery();
	const [addMetric] = useMetric();
	const { openBankAuthenticationModal } = useAppContext();
	const gateState = useBBASveaAuthenticationGate();
	const sveaAuth = useNewSveaAuthentication();

	const currentPsuInfo = gateState.type === "authenticated" ? gateState.currentPsuInfo : undefined;

	const signAndPayState = useSignAndPay({
		paymentValidationRules: props.paymentValidationRules,
		currentPsuInfo,
		onSubmit: () => {
			addMetric(AnalyticsEventCategory.Bba, "SignedPayment", "FromSignAndPayPopover");
			props.onClose();
		},
	});

	const isUnauthenticated = gateState.type === "unauthenticated";
	const isNoAccess = gateState.type === "notPermitted" || signAndPayState.restrictedAccessState === "noAccess";

	// Unauthenticated: gate replaces the entire footer (sign + view payments hidden)
	// No access: sign button hidden, view payments shown
	// Partial access: both buttons shown
	const hideSignButton = isUnauthenticated || isNoAccess;

	const forceDisableSign =
		gateState.type === "notPermitted" ||
		gateState.type === "unauthenticated" ||
		signAndPayState.restrictedAccessState === "noAccess";

	const onClose = () => {
		if (!signAndPayState.isPolling && !signAndPayState.authorizeRequest.isLoading) {
			props.onClose();
		}
	};

	useEffect(() => {
		if (!props.visible) {
			return;
		}
		signAndPayState.refreshPaymentListAndSummary();

		const hasInprogressPaymentSigning = readPaymentSigningProgressInfoFromLocalStorage();
		if (readSveaAuthPollingTokenFromLocalStorage() && hasInprogressPaymentSigning?.hasInitiated) {
			openBankAuthenticationModal();
		} else if (hasInprogressPaymentSigning && hasInprogressPaymentSigning.paymentGroupIds) {
			signAndPayState.setSelectedPaymentGroupIds(hasInprogressPaymentSigning.paymentGroupIds);
			// If there is a interrupted polling session saved to local storage we resume it here.
			// we check for mobile because, in mobile we display the sign & pay modal instead of pop-over. In those two components we use the hook
			// useSignAndPay differently. Hence, we need to resume the polling process here if its not mobile.
			if (hasInprogressPaymentSigning.isPolling && !isMobile) {
				signAndPayState.executeAuthorize();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.visible]);

	return (
		<>
			{isMobile ? (
				<SignAndPayModalInner
					{...props}
					{...signAndPayState}
					showTransactionsAndPaymentsLinks
					onSubmit={noop}
					onClose={onClose}
				/>
			) : (
				<SignAndPayPopoverInner
					{...props}
					{...signAndPayState}
					forceDisableSign={forceDisableSign}
					hideSignButton={hideSignButton}
					gateType={gateState.type}
					restrictedAccessState={signAndPayState.restrictedAccessState}
					gateContent={
						isUnauthenticated ? <SignAndPayLoginGate onAuthenticate={() => sveaAuth.authenticate()} /> : undefined
					}
					onClose={onClose}
				/>
			)}
		</>
	);
};
